import { PendingChange } from '../../configs/tableConfig';
import React, { useState, useEffect, useCallback } from 'react';
import { useSync } from '../../contexts/SyncContext';
import { getSyncAdapter } from '../../libs/syncClient';
import { esRechazadoPorPermisos } from '../../libs/syncErrors';

interface QueueViewerProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

const QueueViewer: React.FC<QueueViewerProps> = ({ isOpen, onClose, className = '' }) => {
  const { sync, ready } = useSync();
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const loadPendingChanges = useCallback(async () => {
    const adapter = getSyncAdapter();
    if (!sync || !adapter) return;

    setLoading(true);
    try {
      const changes = await adapter.getPendingChanges();
      setPendingChanges(changes);
    } catch (error) {
      console.error('Error loading pending changes:', error);
    } finally {
      setLoading(false);
    }
  }, [sync]);

  useEffect(() => {
    if (isOpen && sync && ready) {
      loadPendingChanges();
    }
  }, [isOpen, sync, ready, loadPendingChanges]);

  const handleRetry = async (_change: PendingChange) => {
    if (!sync) return;

    // Reintenta la cola completa con el SyncManager: aplica batching,
    // stripLocalFields y el lock compartido SW↔app.
    try {
      await sync.syncToRemote();
      loadPendingChanges();
    } catch (error) {
      console.error('Retry failed:', error);
      alert(`Error de sincronizacion: ${error}`);
    }
  };

  const handleDelete = async (change: PendingChange) => {
    const adapter = getSyncAdapter();
    if (!sync || !adapter || !change.id) return;

    try {
      await adapter.removePendingChange(change.id);
      loadPendingChanges();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'INSERT': return 'text-success bg-success/10 border border-success/20';
      case 'UPDATE': return 'text-info bg-info/10 border border-info/20';
      case 'DELETE': return 'text-danger bg-danger/10 border border-danger/20';
      default: return 'text-sage bg-paper-2 border border-hairline';
    }
  };

  const getRecordSummary = (record: Record<string, any> | string) => {
    if (typeof record === 'string') {
      return record;
    }

    // Try to get a meaningful identifier
    const id = record.id || record.caravana || record.nombre || JSON.stringify(record).slice(0, 50) + '...';
    return id;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className={`rounded-lg border border-hairline bg-paper shadow-overlay max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden ${className}`}>
        <div className="flex items-center justify-between p-6 border-b border-hairline">
          <h2 className="font-display text-xl font-semibold text-ink">Cola de Sincronización</h2>
          <button
            onClick={onClose}
            className="text-sage hover:text-ink transition-colors"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest"></div>
              <span className="ml-2 text-sage">Cargando...</span>
            </div>
          ) : pendingChanges.length === 0 ? (
            <div className="text-center py-8 text-sage">
              <svg className="mx-auto h-12 w-12 text-sage" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-ink">Cola vacía</h3>
              <p className="mt-1 text-sm text-sage">No hay operaciones pendientes de sincronización.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingChanges.map((change, index) => (
                <div key={change.id || index} className="border border-hairline rounded-lg p-4 bg-paper-2/40">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(change.action)}`}>
                        {change.action}
                      </span>
                      <span className="text-sm font-medium text-ink capitalize">
                        {change.table}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleRetry(change)}
                        className="px-3 py-1 text-xs font-medium text-forest hover:bg-forest/10 rounded transition-colors"
                      >
                        Reintentar
                      </button>
                      <button
                        onClick={() => handleDelete(change)}
                        className="px-3 py-1 text-xs font-medium text-danger hover:bg-danger/10 rounded transition-colors"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-sage bg-white p-2 rounded border border-hairline">
                    <strong>ID/Registro:</strong> {getRecordSummary(change.record)}
                  </div>
                  {change.lastError && (
                    <div
                      className={`mt-2 text-xs p-2 rounded border ${
                        esRechazadoPorPermisos(change.lastError)
                          ? 'bg-danger/10 border-danger/20 text-danger'
                          : 'bg-warning/10 border-warning/20 text-warning'
                      }`}
                    >
                      {esRechazadoPorPermisos(change.lastError) ? (
                        <>
                          <strong>Rechazado por permisos.</strong> El servidor no aceptó este
                          cambio con tu rol: no se va a reintentar. Si el dato es válido,
                          pedile a un dueño/admin que lo cargue, o eliminálo de la cola.
                        </>
                      ) : (
                        <>
                          <strong>Error:</strong> {change.lastError}
                          {change.retryCount ? ` (reintento ${change.retryCount})` : ''}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end p-6 border-t border-hairline bg-paper-2/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-ink bg-paper border border-hairline rounded-md hover:bg-hairline/50 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default QueueViewer;